import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { FormError } from '@/components/ui/form-error';
import { PageLoading } from '@/components/ui/loading';
import { Plus, Trash2, Save, Send, Lock } from 'lucide-react';
import type { Project, Shift, LabourCategory, LabourRate, EligibleProjectManager } from '@/lib/types';
import { cn } from '@/lib/utils';
import { requiredText, optionalText, dateFieldRule, headcountRule, hoursRule, rateRule, sameDaySuperRefine, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField, handleApiSubmitError } from '@/lib/form-validation';

type SaveIntent = 'DRAFT' | 'SUBMIT';

const schema = z.object({
  projectId: requiredText('Project', 100),
  blockId: z.string().optional(),
  assignedPmUserId: z.string().optional(),
  startDate: dateFieldRule('Start date'),
  endDate: dateFieldRule('End date'),
  shiftId: z.string().optional(),
  workLocation: optionalText(200),
  activityDescription: optionalText(2000),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
  notes: optionalText(2000),
  items: z.array(z.object({
    workerCategoryId: requiredText('Worker Category', 100),
    headcount: headcountRule,
    hours: hoursRule,
    rate: rateRule,
    notes: z.string().optional(),
  })).min(1, 'At least one labour item is required.'),
}).superRefine(sameDaySuperRefine('startDate', 'endDate'));

type FormData = z.infer<typeof schema>;
type ItemFormRow = { workerCategoryId: string; headcount: number; hours: number; rate: number; notes?: string };

export default function RequirementsFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  const saveIntentRef = useRef<SaveIntent>('DRAFT');

  const { register, handleSubmit, control, watch, setValue, reset, setError, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { priority: 'normal', items: [{ workerCategoryId: '', headcount: 1, hours: 0, rate: 0 }] },
    mode: 'onSubmit',
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  const { data: projects } = useQuery({ queryKey: ['p'], queryFn: async () => { const r = await api.get<any>('/projects?availability=active'); return (r.data ?? []) as Project[]; } });
  const { data: shifts } = useQuery({ queryKey: ['s'], queryFn: async () => { const r = await api.get<any>('/shifts'); return (r.data ?? []) as Shift[]; } });
  const { data: categories } = useQuery({ queryKey: ['wc'], queryFn: async () => { const r = await api.get<any>('/labour-categories/all'); return (r.data ?? []) as LabourCategory[]; } });
  const { data: rates } = useQuery({ queryKey: ['r'], queryFn: async () => { const r = await api.get<any>('/rates'); return (r.data ?? []) as LabourRate[]; } });

  const watchedProjectId = watch('projectId') as string | undefined;
  const { data: blocks } = useQuery({
    queryKey: ['blocks', watchedProjectId],
    queryFn: async () => { const r = await api.get<any>(`/blocks/all?projectId=${watchedProjectId}`); return (r.data ?? []) as { id: string; name: string; project_id: string; }[]; },
    enabled: Boolean(watchedProjectId),
  });

  const { data: eligiblePms, isLoading: pmLoading, isError: pmError, refetch: refetchPms } = useQuery({
    queryKey: ['eligible-pms'],
    queryFn: async () => { const r = await api.get<any>('/requirements/eligible-pms'); return (r.data ?? []) as EligibleProjectManager[]; },
    retry: 0,
  });

  const { data: existing, isLoading: loadingExisting, isError: isErrorExisting } = useQuery({
    queryKey: ['requirement', id],
    queryFn: async () => { const r = await api.get<{ success: boolean; data: any }>(`/requirements/${id}`); return r.data; },
    enabled: isEdit,
  });

  const watchedItems = watch('items') as ItemFormRow[] | undefined;

  // Resolve the selected shift's normal hours from the shift master. The API
  // exposes camelCase `normalHours` (web type) while the raw shifts endpoint
  // returns snake_case `normal_hours`, so both are accepted.
  function shiftNormalHours(shiftId?: string): number | null {
    if (!shiftId) return null;
    const shift = shifts?.find((s: Shift) => s.id === shiftId);
    if (!shift) return null;
    const hours = (shift as any).normalHours ?? (shift as any).normal_hours;
    return typeof hours === 'number' && Number.isFinite(hours) ? hours : null;
  }

  // Apply a shift's hours to every labour item. Fired from the Shift select
  // onValueChange (only on user change, never on mount) so we never destabilize
  // react-hook-form's validation during initial render.
  function applyShiftHoursToItems(shiftId?: string) {
    const hours = shiftNormalHours(shiftId);
    if (hours === null) return;
    watchedItems?.forEach((_, idx) => {
      setValue(`items.${idx}.hours`, hours, { shouldValidate: false, shouldDirty: true });
    });
  }

  useEffect(() => {
    watchedItems?.forEach((item, idx) => {
      if (item?.workerCategoryId && !item?.rate) {
        const rate = rates?.find((r: LabourRate) => (r.workerCategoryId ?? r.tradeId) === item.workerCategoryId && r.isActive);
        if (rate) {
          setValue(`items.${idx}.rate`, rate.ratePerHour);
        }
      }
    });
  }, [watchedItems, rates, setValue]);

  // ── Same-day dates: auto-sync End Date to Start Date ──────────────
  const watchedStartDate = watch('startDate') as string | undefined;

  useEffect(() => {
    if (watchedStartDate) {
      setValue('endDate', watchedStartDate, { shouldValidate: false, shouldDirty: true });
    } else {
      setValue('endDate', '', { shouldValidate: false, shouldDirty: true });
    }
  }, [watchedStartDate, setValue]);

  // Clear block when project changes (create mode only)
  useEffect(() => {
    if (!isEdit && watchedProjectId) {
      setValue('blockId', '', { shouldValidate: false, shouldDirty: true });
    }
  }, [watchedProjectId, isEdit, setValue]);

  // Populate form in edit mode — normalize End Date to Start Date
  useEffect(() => {
    if (existing) {
      const savedStartDate = existing.startDate || '';
      reset({
        projectId: String(existing.projectId ?? existing.project_id ?? ''),
        blockId: String(existing.blockId ?? existing.block_id ?? ''),
        assignedPmUserId: String(existing.assignedPmUserId ?? existing.assigned_pm_user_id ?? ''),
        startDate: savedStartDate,
        endDate: savedStartDate,
        shiftId: String(existing.shiftId ?? existing.shift_id ?? ''),
        workLocation: existing.workLocation ?? existing.work_location ?? '',
        activityDescription: existing.activityDescription ?? existing.activity_description ?? '',
        priority: existing.priority || 'normal',
        notes: existing.notes || '',
        items: (existing.items || []).map((item: any) => ({
          workerCategoryId: String(item.workerCategoryId ?? item.worker_category_id ?? item.trade_id ?? ''),
          headcount: item.count ?? item.requiredHeadcount ?? item.required_headcount ?? 1,
          hours: item.hours ?? item.plannedHours ?? item.planned_hours ?? 0,
          rate: item.rate ?? item.plannedRate ?? item.planned_rate ?? 0,
        })),
      });
    }
  }, [existing, reset]);

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      const intent = saveIntentRef.current;
      const payload = {
        projectId: data.projectId,
        blockId: data.blockId || undefined,
        assignedPmUserId: data.assignedPmUserId || undefined,
        startDate: data.startDate,
        endDate: data.endDate,
        shiftId: data.shiftId || undefined,
        workLocation: data.workLocation || undefined,
        activityDescription: data.activityDescription || undefined,
        priority: data.priority,
        notes: data.notes || undefined,
        intent,
        labourItems: data.items.map(item => ({
          workerCategoryId: item.workerCategoryId,
          headcount: item.headcount,
          hours: item.hours,
          rate: item.rate,
        })),
      };
      if (isEdit) {
        return api.put(`/requirements/${id}`, payload);
      }
      return api.post('/requirements', payload);
    },
    onSuccess: () => {
      const intent = saveIntentRef.current;
      addToast(
        intent === 'DRAFT' ? 'Departmental Labour-Requirement saved as draft.' : 'Departmental Labour-Requirement submitted to Manager for approval.',
        intent === 'DRAFT' ? 'info' : 'success'
      );
      queryClient.invalidateQueries({ queryKey: ['requirements'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['report-project-summary'] });
      queryClient.invalidateQueries({ queryKey: ['report-category-detail'] });
      queryClient.invalidateQueries({ queryKey: ['report-budget-actual'] });
      queryClient.invalidateQueries({ queryKey: ['report-multi-summary'] });
      queryClient.invalidateQueries({ queryKey: ['requirement', id] });
      navigate('/requirements');
    },
    onError: (err: any) => {
      handleApiSubmitError(err, { addToast, setError: (name, error) => setError(name as any, error) });
    },
    onSettled: () => {
      saveIntentRef.current = 'DRAFT';
    },
  });

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const onSubmit = (data: FormData) => {
    if (saveIntentRef.current === 'SUBMIT' && !data.assignedPmUserId) {
      setError('assignedPmUserId', { message: 'A Project Manager must be selected before submitting.' });
      addToast('A Project Manager must be selected before submitting.', 'error');
      return;
    }
    mutation.mutate(data);
  };

  const isPending = mutation.isPending;

  const savedPmId = watch('assignedPmUserId') || '';
  const savedPmName = existing?.assignedPmName ?? existing?.assigned_pm_name ?? null;
  const savedPmEmail = existing?.assignedPmEmail ?? existing?.assigned_pm_email ?? null;
  const pmOptionsLoaded = !pmLoading && !pmError && !!eligiblePms;
  const savedPmNotEligible = pmOptionsLoaded && !!savedPmId && !eligiblePms!.some((p) => p.id === savedPmId);
  const pmOptions = [
    ...(eligiblePms ?? []).map((p) => ({ value: p.id, label: `${p.name} — ${p.email}` })),
    ...(savedPmNotEligible && savedPmName ? [{ value: savedPmId, label: `${savedPmName} — ${savedPmEmail ?? ''} (previously assigned)` }] : []),
  ];

  if (isEdit && loadingExisting) return <PageLoading />;
  if (isEdit && isErrorExisting) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <h2 className="text-xl font-bold text-primary">Not Found</h2>
        <p className="text-secondary mt-2">This requirement could not be found or you do not have access.</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate('/requirements')}>Back to Departmental Labour-Requirement</Button>
      </div>
    );
  }

  if (!projects || !categories) return <PageLoading />;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">{isEdit ? 'Edit Departmental Labour-Requirement' : 'New Departmental Labour-Requirement'}</h1>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Project *</Label>
              <Select value={watch('projectId') || ''} onValueChange={v => setValue('projectId', v)} disabled={isEdit}>
                <SelectTrigger data-field-name="projectId" aria-invalid={errors.projectId ? true : undefined} aria-describedby={errors.projectId ? 'projectId-error' : undefined}><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p: Project) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormError id="projectId-error" message={errors.projectId?.message} />
            </div>
            <div>
              <Label>Block</Label>
              <Select value={watch('blockId') || ''} onValueChange={v => setValue('blockId', v)} disabled={!watchedProjectId}>
                <SelectTrigger data-field-name="blockId"><SelectValue placeholder="Select block" /></SelectTrigger>
                <SelectContent>
                  {blocks?.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project Manager *</Label>
              {pmLoading ? (
                <p className="text-sm text-secondary h-10 flex items-center">Loading Project Managers...</p>
              ) : pmError ? (
                <div className="space-y-2">
                  <p className="text-sm text-error">Unable to load eligible Project Managers.</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => refetchPms()}>Retry</Button>
                </div>
              ) : (
                <SearchableSelect
                  value={watch('assignedPmUserId') || ''}
                  onValueChange={v => setValue('assignedPmUserId', v)}
                  options={pmOptions}
                  placeholder="Select Project Manager"
                  searchPlaceholder="Search by name or email..."
                  emptyMessage="No eligible Project Managers"
                  data-field-name="assignedPmUserId"
                  aria-invalid={errors.assignedPmUserId ? true : undefined}
                  aria-describedby={errors.assignedPmUserId ? 'assignedPmUserId-error' : undefined}
                />
              )}
              {savedPmNotEligible && (
                <p className="text-xs text-error mt-1">The previously assigned Project Manager is no longer active or eligible. Select a new one before submitting.</p>
              )}
              <FormError id="assignedPmUserId-error" message={errors.assignedPmUserId?.message} />
            </div>
            <div>
              <Label>Start Date *</Label>
              <Input type="date" {...register('startDate')} aria-invalid={errors.startDate ? true : undefined} aria-describedby={errors.startDate ? 'startDate-error' : undefined} className={cn(errors.startDate && 'border-error')} />
              <FormError id="startDate-error" message={errors.startDate?.message} />
            </div>
            <div>
              <Label>End Date *</Label>
              <div className="relative">
                <Input type="date" value={watch('endDate') || ''} readOnly tabIndex={-1} aria-invalid={errors.endDate ? true : undefined} aria-describedby={errors.endDate ? 'endDate-error endDate-locked' : 'endDate-locked'} className={cn('bg-surface-secondary cursor-not-allowed pr-9', errors.endDate && 'border-error')} />
                <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
              </div>
              <p id="endDate-locked" className="text-xs text-muted mt-1">End Date is automatically the same as Start Date.</p>
              <FormError id="endDate-error" message={errors.endDate?.message} />
            </div>
            <div>
              <Label>Shift</Label>
              <Select value={watch('shiftId') || ''} onValueChange={v => { setValue('shiftId', v); applyShiftHoursToItems(v); }}>
                <SelectTrigger data-field-name="shiftId"><SelectValue placeholder="Select shift" /></SelectTrigger>
                <SelectContent>
                  {shifts?.map((s: Shift) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Work Area</Label><Input {...register('workLocation')} placeholder="e.g. Floor 5 North" /></div>
            <div className="md:col-span-2 min-w-0">
              <Label>Activity Description</Label>
              <Textarea {...register('activityDescription')} placeholder="Describe the work..." />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={watch('priority') || 'normal'} onValueChange={v => setValue('priority', v as any)}>
                <SelectTrigger data-field-name="priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input {...register('notes')} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Labour Items</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => append({ workerCategoryId: '', headcount: 1, hours: shiftNormalHours(watch('shiftId')) ?? 0, rate: 0 })}>
              <Plus size={14} /> Add Item
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {errors.items && !Array.isArray(errors.items) && <FormError message={errors.items.message} />}
            {fields.map((field, idx) => (
              <div key={field.id} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3 items-end border rounded-lg p-3 min-w-0">
                <div className="sm:col-span-2 md:col-span-3 min-w-0">
                  <Label>Worker Category *</Label>
                  <Select value={watch(`items.${idx}.workerCategoryId`) || ''} onValueChange={v => setValue(`items.${idx}.workerCategoryId`, v)}>
                    <SelectTrigger className="h-auto min-h-10" data-field-name={`items.${idx}.workerCategoryId`} aria-invalid={errors.items?.[idx]?.workerCategoryId ? true : undefined} aria-describedby={errors.items?.[idx]?.workerCategoryId ? `items-${idx}-workerCategoryId-error` : undefined}><SelectValue className="min-w-0" placeholder="Select worker category" /></SelectTrigger>
                    <SelectContent className="min-w-[16rem]">
                      {categories?.map((c: LabourCategory) => <SelectItem key={c.id} value={c.id}>{c.name} — {(c.skill_level || c.skillLevel || 'UNCLASSIFIED').replace('_', ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormError id={`items-${idx}-workerCategoryId-error`} message={errors.items?.[idx]?.workerCategoryId?.message} />
                </div>
                <div className="min-w-0 md:col-span-2">
                  <Label>Headcount *</Label>
                  <Input type="number" {...register(`items.${idx}.headcount`)} min={1} aria-invalid={errors.items?.[idx]?.headcount ? true : undefined} aria-describedby={errors.items?.[idx]?.headcount ? `items-${idx}-headcount-error` : undefined} className={cn(errors.items?.[idx]?.headcount && 'border-error')} />
                  <FormError id={`items-${idx}-headcount-error`} message={errors.items?.[idx]?.headcount?.message} />
                </div>
                <div className="min-w-0 md:col-span-2">
                  <Label>Hours *</Label>
                  <Input type="number" {...register(`items.${idx}.hours`)} min={1} readOnly={!!watch('shiftId')} aria-invalid={errors.items?.[idx]?.hours ? true : undefined} aria-describedby={errors.items?.[idx]?.hours ? `items-${idx}-hours-error` : undefined} className={cn(!!watch('shiftId') && 'cursor-not-allowed bg-surface-secondary', errors.items?.[idx]?.hours && 'border-error')} />
                  <FormError id={`items-${idx}-hours-error`} message={errors.items?.[idx]?.hours?.message} />
                </div>
                <div className="min-w-0 md:col-span-2">
                  <Label>Rate (₹/hr)</Label>
                  <Input type="number" step="0.01" {...register(`items.${idx}.rate`)} aria-invalid={errors.items?.[idx]?.rate ? true : undefined} aria-describedby={errors.items?.[idx]?.rate ? `items-${idx}-rate-error` : undefined} className={cn(errors.items?.[idx]?.rate && 'border-error')} />
                  <FormError id={`items-${idx}-rate-error`} message={errors.items?.[idx]?.rate?.message} />
                </div>
                <div className="min-w-0 md:col-span-2">
                  <p className="text-xs text-secondary mb-1">Amount</p>
                  <p className="text-sm font-medium">
                    ₹{((watchedItems?.[idx]?.hours || 0) * (watchedItems?.[idx]?.rate || 0) * (watchedItems?.[idx]?.headcount || 0)).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="md:col-span-1">
                  {fields.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => remove(idx)}>
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <Button type="button" variant="outline" className="w-full sm:w-auto" disabled={isPending} onClick={() => navigate('/requirements')}>Cancel</Button>
          <Button type="submit" variant="secondary" className="w-full sm:w-auto" disabled={isPending} loading={isPending && saveIntentRef.current === 'DRAFT'} onClick={() => { saveIntentRef.current = 'DRAFT'; }}>
            <Save size={16} /> Save Draft
          </Button>
          <Button type="submit" className="w-full sm:w-auto" disabled={isPending} loading={isPending && saveIntentRef.current === 'SUBMIT'} onClick={() => { saveIntentRef.current = 'SUBMIT'; }}>
            <Send size={16} /> Submit
          </Button>
        </div>
      </form>
    </div>
  );
}


